import { Badge } from '../Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../Card';
import { CalendarDays, Plus, Bug, Zap, AlertCircle } from 'lucide-react';

interface ChangelogEntry {
  version: string;
  date: string;
  changes: {
    type: 'added' | 'fixed' | 'changed' | 'removed';
    description: string;
  }[];
}

const changelogData: ChangelogEntry[] = [
  {
    version: '4.2.5',
    date: '2025-05-27',
    changes: [{ type: 'added', description: 'Social login with Google and Microsoft' }],
  },
  {
    version: '4.2.4',
    date: '2025-05-22',
    changes: [{ type: 'fixed', description: 'Propagate tags and update workflow name' }],
  },
  {
    version: '4.2.3',
    date: '2025-05-16',
    changes: [
      { type: 'added', description: 'Display parent question steps' },
      { type: 'added', description: 'Hover text for listing column headers' },
      { type: 'added', description: 'Persist page size preference' },
      { type: 'fixed', description: 'Fix feedback submission form' },
    ],
  },
  {
    version: '4.2.2',
    date: '2025-05-11',
    changes: [
      { type: 'added', description: 'Add summary insights' },
      { type: 'fixed', description: 'Make refresh button always available' },
    ],
  },
  {
    version: '4.2.1',
    date: '2025-04-26',
    changes: [{ type: 'fixed', description: 'Virtualize Cloud listing' }],
  },

  {
    version: '4.2.0',
    date: '2025-04-21',
    changes: [
      { type: 'added', description: 'Design refresh' },
      { type: 'added', description: 'Support for Mithrl Cloud' },
      { type: 'fixed', description: 'Address performance issue with SRA extraction' },
    ],
  },
];

const getChangeTypeIcon = (type: string) => {
  switch (type) {
    case 'added':
      return <Plus className="h-6 w-6" />;
    case 'fixed':
      return <Bug className="h-6 w-6" />;
    case 'changed':
      return <Zap className="h-6 w-6" />;
    case 'removed':
      return <AlertCircle className="h-6 w-6" />;
    default:
      return null;
  }
};

const getChangeTypeBadge = (type: string) => {
  const variants = {
    added: 'bg-green-900 text-green-200 hover:bg-green-900',
    fixed: 'bg-blue-900 text-blue-200 hover:bg-blue-900',
    changed: 'bg-yellow-900 text-yellow-200 hover:bg-yellow-900',
    removed: 'bg-red-900 text-red-200 hover:bg-red-900',
  };

  return variants[type as keyof typeof variants] || 'bg-gray-100 text-gray-800';
};

export default function Changelog() {
  return (
    <div className="mx-auto mb-8 p-4">
      <div className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold text-white">Changelog</h1>
        <p className="text-lg text-gray-300">Latest updates and improvements</p>
      </div>

      <div className="space-y-6">
        {changelogData.map((entry, index) => (
          <Card key={entry.version} className="relative border-gray-700 bg-gray-800">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-semibold text-white">Version {entry.version}</CardTitle>
                <div className="flex items-center text-sm text-gray-400">
                  <CalendarDays className="mr-1 h-4 w-4" />
                  {new Date(entry.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </div>
              </div>
            </CardHeader>

            <CardContent>
              <div className="space-y-3">
                {entry.changes.map((change, changeIndex) => (
                  <div key={changeIndex} className="flex items-start gap-3 rounded-lg bg-gray-700 p-3">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Badge
                        variant="secondary"
                        className={`${getChangeTypeBadge(change.type)} flex items-center gap-1 text-sm font-medium`}
                      >
                        {getChangeTypeIcon(change.type)}
                      </Badge>
                      <span className="text-sm text-gray-200">{change.description}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>

            {index < changelogData.length - 1 && (
              <div className="absolute bottom-0 left-6 h-6 w-px translate-y-full transform bg-gray-600"></div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
